import { memo, ReactNode, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import { armApiRef } from '../../../../api/ArmApiClient';

type SelectOption = { label: string; value: string };

const makeOptionsForObjects = (
  objects: { id: string; name: string }[],
): SelectOption[] =>
  [...objects]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(x => ({ label: x.name, value: x.id }));

const useStyles = makeStyles()({
  formControl: {
    display: 'flex',
    flexDirection: 'row',
  },
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
  },
  inputElement: {
    flex: '1 1 auto',
  },
  inputElementSelectMulti: {
    '&&': {
      flexWrap: 'wrap',
      padding: '6px',
    },
  },
});

const SelectInput = ({
  id,
  name,
  onChange,
  value,
  options,
  helperText,
  error,
}: {
  id: string;
  name: string;
  value: string[];
  options: (inputValue?: string) => Promise<SelectOption[]>;
  onChange: (values: string[]) => void;
  helperText?: string | ReactNode;
  error?: boolean;
}) => {
  const { classes } = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [availableOptions, setAvailableOptions] = useState<SelectOption[]>([]);
  const [optionsState, optionsFetch] = useAsyncFn(options, [options]);

  useEffect(() => {
    if (optionsState.loading) return;
    if (optionsState.value) {
      setAvailableOptions(optionsState.value);
    } else {
      optionsFetch();
    }
  }, [value, optionsState, optionsFetch]);

  const getOptionLabel = (option: string): string =>
    availableOptions.find(x => x.value === option)?.label ?? option;

  return (
    <FormControl fullWidth variant="standard" className={classes.formControl}>
      <Box className={classes.inputBox}>
        <Autocomplete
          id={id}
          multiple
          options={availableOptions.map(o => o.value)}
          openOnFocus
          onChange={(_, v) => onChange(v)}
          onBlur={() => onChange(value)}
          onInputChange={(e, v) => {
            setInputValue(v);
            if (v !== inputValue && e?.type === 'change') optionsFetch(v);
          }}
          value={value}
          isOptionEqualToValue={(option, val) => option === val}
          getOptionLabel={getOptionLabel}
          getOptionKey={option => option}
          renderInput={params => (
            <>
              <input
                tabIndex={-1}
                autoComplete="off"
                style={{ opacity: 0, height: 0, padding: 0, margin: 0 }}
                value={value?.length ? 'ok' : ''}
                required
                readOnly
              />
              <TextField
                label="Security Groups*"
                {...params}
                className={classes.inputElement}
                name={name}
                error={error}
                variant="outlined"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    className: classes.inputElementSelectMulti,
                  },
                  formHelperText: { disabled: false },
                }}
                helperText={helperText}
              />
            </>
          )}
        />
      </Box>
    </FormControl>
  );
};

type SecurityGroupsSectionProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

export const SecurityGroupsSection = memo(
  ({ value, onChange }: SecurityGroupsSectionProps) => {
    const armApi = useApi(armApiRef);
    const [error, setError] = useState(false);

    const getSecurityObjects = async (ids: string[], match?: string) => {
      const groups = [];
      if (ids.length > 0) {
        groups.push(...(await armApi.getGroups({ ids })));
      }
      groups.push(
        ...(await armApi.getGroups(match ? { match } : { isMember: true })),
      );
      const seen = new Set<string>();
      return groups.filter(o => !seen.has(o.id) && seen.add(o.id));
    };

    const onValuesChange = (change: string[]) => {
      onChange(change);
      setError(change.length === 0);
    };

    return (
      <FormControl
        fullWidth
        variant="standard"
        size="small"
        error={!value?.length}
      >
        <SelectInput
          id="security-object-ids"
          name="securityObjectIds"
          value={value ?? []}
          options={async match =>
            makeOptionsForObjects(await getSecurityObjects(value ?? [], match))
          }
          onChange={onValuesChange}
          error={error}
        />
        {error ? (
          <FormHelperText error>Security groups required</FormHelperText>
        ) : (
          <FormHelperText>
            <span>
              Select the{' '}
              <Link
                target="_blank"
                underline="hover"
                href="https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation/#create-a-tenant"
              >
                Azure Security groups
              </Link>{' '}
              that will have access to this tenant
            </span>
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);
